import { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';
import { useQuery } from '@apollo/client/react';
import { GET_FAVOURITES_LIST, GetFavouritesListData } from '@jwp/ott-common/src/graphql/query/favorite.queries';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import type { PlaylistItem, Playlist } from '@jwp/ott-common/types/playlist';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Favorites from '../../components/Favorites/Favorites';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Button from '../../components/Button/Button';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';

import styles from './FavouritesPage.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import ErrorPage from '../../components/ErrorPage/ErrorPage';

const FavouritesPage = (): JSX.Element => {
  const favoritesController = getModule(FavoritesController);
  const apiService = getModule(ApiService);
  const [clearFavoritesOpen, setClearFavoritesOpen] = useState(false);
  const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);

  const { accessModel, favoritesListId } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesListId: s.config.features?.favoritesList,
    }),
    shallow,
  );
  const navigate = useNavigate();

  const { t } = useTranslation(['error', 'user', 'common']);
  const breakpoint = useBreakpoint();

  const { user: customer, subscription, loading: accountLoading } = useAccountStore();
  const { isAnalyticsEnable, isCognitoEnabled, isKatonCognitoEnabled } = useAppConfig();
  const isLoggedIn = !!customer;
  const userId = isLoggedIn ? customer.id : 'Guest user';
  const deviceType = useGADeviceType();

  // Local/Store logic
  const favoritesFromStore = useFavoritesStore((state) => state.getPlaylist());
  const showFavourites = useFavoritesStore((state) => state.showFavourites);

  // Cognito/GraphQL logic
  const { data, loading: favoritesLoading, refetch, fetchMore } = useQuery<GetFavouritesListData>(GET_FAVOURITES_LIST, {
    variables: { first: 25, offset: 0 },
    skip: !isLoggedIn || (isCognitoEnabled === false && isKatonCognitoEnabled === false),
    fetchPolicy: 'network-only'
  });

  const [loadingMore, setLoadingMore] = useState(false);
  const totalCount = data?.getFavouritesList?.totalCount || 0;
  const hasNextPage = (data?.getFavouritesList?.nodes?.length || 0) < totalCount;

  const handleLoadMore = async () => {
    if (loadingMore || !hasNextPage) return;
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          offset: data?.getFavouritesList?.nodes?.length || 0,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            getFavouritesList: {
              ...fetchMoreResult.getFavouritesList,
              nodes: [
                ...(prev.getFavouritesList.nodes || []),
                ...(fetchMoreResult.getFavouritesList.nodes || []),
              ],
            },
          };
        },
      });
    } catch (error) {
      console.error('Error fetching more favorites:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const { setAncestor, reset } = useNavigationStack();

  useEffect(() => {
    reset();
    setAncestor(`${window.location.pathname}${window.location.search}`);
  }, [reset, setAncestor]);

  const favoriteMediaIds = useMemo(() => {
    return data?.getFavouritesList?.nodes?.map(node => node?.fJwMediaId) || [];
  }, [data]);

  useEffect(() => {
    const fetchMediaDetails = async () => {
      if ((isCognitoEnabled || isKatonCognitoEnabled) && favoriteMediaIds?.length > 0 && favoritesListId) {
        // Find which IDs we don't have in the playlist yet
        const existingIds = new Set(playlist.map(item => item.mediaid));
        const newIds = favoriteMediaIds.filter(id => !existingIds.has(id));

        if (newIds.length === 0 && favoriteMediaIds.length > 0 && playlist.length === 0) {
          // Initial load fallback
          setPlaylistLoading(true);
          try {
            const mediaItems = await apiService.getMediaByWatchlist(favoritesListId, favoriteMediaIds);
            setPlaylist(mediaItems || []);
          } catch (error) {
            console.error('Failed to fetch favorite media details', error);
          } finally {
            setPlaylistLoading(false);
          }
          return;
        }

        if (newIds.length > 0) {
          // Don't set global playlistLoading to true here to avoid UI jerk/reload feel
          try {
            const mediaItems = await apiService.getMediaByWatchlist(favoritesListId, newIds);
            if (mediaItems) {
              setPlaylist(prev => [...prev, ...mediaItems]);
            }
          } catch (error) {
            console.error('Failed to fetch favorite media details (incremental)', error);
          }
        }
      } else if ((isCognitoEnabled || isKatonCognitoEnabled) && favoriteMediaIds?.length === 0) {
        setPlaylist([]);
      }
    };

    fetchMediaDetails();
  }, [favoriteMediaIds, favoritesListId, apiService, isCognitoEnabled, isKatonCognitoEnabled]);

  useEffect(() => {
    if (isAnalyticsEnable) {
      sendGAEvent('screen_view', {
        firebase_screen_class: 'Favourites',
        firebase_screen: 'Favourites',
        device_type: deviceType,
        userid_no: userId,
      });
    }
  }, []);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'Down' : 'Up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!accountLoading && !customer) {
      navigate('/', { replace: true });
    }
  }, [navigate, customer, accountLoading]);

  const currentPlaylist = useMemo(() => {
    if (isCognitoEnabled || isKatonCognitoEnabled) {
      return {
        kind: 'WATCHLIST',
        playlist: playlist,
        title: 'Favorites',
        feedid: favoritesListId,
      } as unknown as Playlist;
    }
    return favoritesFromStore;
  }, [isCognitoEnabled, isKatonCognitoEnabled, playlist, favoritesFromStore, favoritesListId]);

  const isPageLoading = accountLoading || ((isCognitoEnabled || isKatonCognitoEnabled) && favoritesLoading && playlist.length === 0) || playlistLoading;

  if (isPageLoading) {
    return (
      <div className={styles.user}>
        <LoadingOverlay inline />
      </div>
    );
  }

  if (!showFavourites) {
    return <ErrorPage title={t('empty_shelves_heading')} message={t('empty_shelves_description')} alignTop={false} />;
  }

  return (
    <div className={styles.favouriteContainer}>
      {playlistLoading && currentPlaylist.length === 0 ? (
        <LoadingOverlay inline />
      ) : (
        <>
          <Favorites 
            playlist={currentPlaylist} 
            onClearFavoritesClick={() => setClearFavoritesOpen(true)} 
            accessModel={accessModel} 
            hasSubscription={!!subscription} 
          />
          {hasNextPage && (isCognitoEnabled || isKatonCognitoEnabled) && (
            <div className={styles.loadMoreContainer}>
              <Button
                label={loadingMore ? t('user:loading') : t('common:load_more')}
                aria-label={t('common:load_more')}
                onClick={handleLoadMore}
                disabled={loadingMore}
                color="default"
                fullWidth={breakpoint < Breakpoint.md}
                size="large"
              />
            </div>
          )}
        </>
      )}
      <ConfirmationDialog
        open={clearFavoritesOpen}
        title={t('user:favorites.clear_favorites_title')}
        body={t('user:favorites.clear_favorites_body')}
        onConfirm={async () => {
          await favoritesController.clear(); 
          setClearFavoritesOpen(false);
          if (isCognitoEnabled || isKatonCognitoEnabled) {
            refetch();
          }
        }}
        onClose={() => setClearFavoritesOpen(false)}
      />
    </div>
  );
};

export default FavouritesPage;
